import { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";

const FOOTER_SERVICES = [
  { slug: "social-media-management", title: "Social Media Management" },
  { slug: "content-strategy", title: "Content Strategy" },
  { slug: "website-design", title: "Website Design" },
  { slug: "ai-consultancy", title: "AI Consultancy" },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await subscribeNewsletter(email);
      toast.success(res.status === "already_subscribed" ? "You are already subscribed" : "Thanks for subscribing");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-white border-t border-[#eee]">
      <div className="td-container py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-baseline gap-1.5 mb-4">
            <span className="font-head text-xl font-semibold text-black">Track</span>
            <span className="font-head text-xl font-semibold text-[#1a1a1a]">Digital</span>
            <span className="w-1.5 h-1.5 rounded-full bg-[#7C6CF0] mb-1" />
          </div>
          <p className="text-[#6b6b6b] leading-relaxed max-w-sm text-[15px]">
            A boutique marketing agency specialising in social media management, strategy, AI and
            digital growth. We bring clarity to your marketing, then deliver it properly.
          </p>
          <form onSubmit={handleSubscribe} className="mt-8 max-w-sm" data-testid="footer-newsletter-form">
            <label className="td-label block mb-3">Newsletter</label>
            <div className="flex items-center border-b border-[#d3d3d3] focus-within:border-[#7C6CF0] transition-colors">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                data-testid="footer-newsletter-input"
                className="flex-1 bg-transparent py-3 text-black placeholder:text-[#a0a0a0] outline-none text-sm"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-newsletter-submit"
                className="text-[#1a1a1a] hover:text-[#7C6CF0] transition-colors p-2 disabled:opacity-50"
                aria-label="Subscribe"
              >
                <ArrowRight size={18} />
              </button>
            </div>
          </form>
        </div>

        <div className="md:col-span-3 md:col-start-7">
          <h4 className="td-label mb-5">Services</h4>
          <ul className="space-y-3">
            {FOOTER_SERVICES.map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="text-[#4a4a4a] hover:text-black transition-colors text-[15px]">
                  {s.title}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/services" className="text-[#7C6CF0] font-medium hover:opacity-70 transition-opacity text-[15px]">
                View all services
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="td-label mb-5">Company</h4>
          <ul className="space-y-3">
            {[
              { label: "About", to: "/about" },
              { label: "Work", to: "/work" },
              { label: "Insights", to: "/insights" },
              { label: "Contact", to: "/contact" },
            ].map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-[#4a4a4a] hover:text-black transition-colors text-[15px]">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="flex gap-4 mt-8">
            <a href="https://www.linkedin.com/company/track-digital-socials" target="_blank" rel="noopener noreferrer" className="text-[#6b6b6b] hover:text-[#7C6CF0] transition-colors" aria-label="Track Digital on LinkedIn"><Linkedin size={20} /></a>
            <a href="https://www.instagram.com/trackdigitalsocials" target="_blank" rel="noopener noreferrer" className="text-[#6b6b6b] hover:text-[#7C6CF0] transition-colors" aria-label="trackdigitalsocials on Instagram"><Instagram size={20} /></a>
            <a href="https://www.facebook.com/profile.php?id=61580263496887" target="_blank" rel="noopener noreferrer" className="text-[#6b6b6b] hover:text-[#7C6CF0] transition-colors" aria-label="Track Digital on Facebook"><Facebook size={20} /></a>
            <a href="mailto:hello@track-digital.com" className="text-[#6b6b6b] hover:text-[#7C6CF0] transition-colors" aria-label="Email Track Digital"><Mail size={20} /></a>
          </div>
        </div>
      </div>

      <div className="td-container py-6 border-t border-[#eee] flex flex-col md:flex-row items-center justify-between gap-3">
        <p className="font-head text-[#1a1a1a]">Style Meets Strategy.</p>
        <div className="flex items-center gap-6 text-[#8a8a8a] text-sm">
          <Link to="/privacy" className="hover:text-[#7C6CF0] transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-[#7C6CF0] transition-colors">Terms</Link>
          <span>© {new Date().getFullYear()} Track Digital</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
